import useQueryState from "Components/Hooks/UseQueryState";
import React, { useEffect, useState } from "react";
import CountUp from "react-countup";
import { Card, CardBody } from "reactstrap";
import { defaultFilter, FilterType } from "../Filters";
import { getTotalPaymentsCount } from "common/services/payments.service";
import Skeleton from "react-loading-skeleton";
import * as moment from "moment";
import { Link } from "react-router-dom";
import PaymentsModal from "pages/Payments/PaymentsModal";

/**
 * This component renders a card with the total number of contracts.
 * The number is fetched from the backend and updated in real-time
 * whenever the filter state changes.
 * The component also displays a loading skeleton while the data is being fetched.
 * @returns {React.ReactElement} A JSX element representing the TotalContracts component.
 */
const TotalContracts = () => {
  const [filter] = useQueryState<FilterType>("filter", defaultFilter);

  const [totalContracts, setTotalContracts] = useState(0);
  const [loading, setLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);

  useEffect(() => {
    setLoading(true);
    const fetchData = async () => {
      try {
        const response: any = await getTotalPaymentsCount({
          mdaId: filter?.mda?.value || undefined,
          regionId: filter?.region?.value || undefined,
          positionTypeId: filter?.positionType?.value || undefined,
          agencyId: filter?.agency?.value || undefined,
          paymentCategoryId: filter?.paymentCategory?.value || undefined,
          thematicAreaId: filter?.thematicArea?.value || undefined,
          sectorId: filter?.sector?.value || undefined,
          startDate: filter?.dateRange?.fromDate
            ? moment(filter.dateRange.fromDate)
                .toDate()
                .toLocaleDateString("en-CA")
            : undefined,
          endDate: filter?.dateRange?.toDate
            ? moment(filter.dateRange.toDate)
                .toDate()
                .toLocaleDateString("en-CA")
            : undefined,
        });

        setTotalContracts(response?.totalCount);
      } catch (error) {
        console.warn("Error fetching total  support", error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [
    filter?.agency?.value,
    filter?.dateRange?.fromDate,
    filter?.dateRange?.toDate,
    filter.mda,
    filter?.positionType?.value,
    filter?.paymentCategory?.value,
    filter?.region?.value,
    filter?.sector?.value,
    filter?.thematicArea?.value,
  ]);

  const toggleModal = (e?: React.MouseEvent) => {
    if (e) {
      e.preventDefault();
    }
    setIsModalOpen(!isModalOpen);
  };

  return (
    <Link to="#" onClick={toggleModal}>
      <Card>
        <CardBody>
          <div className="d-flex align-items-center">
            <div className="avatar-sm flex-shrink-0">
              <span className="avatar-title bg-light text-primary rounded-circle fs-3 material-shadow">
                <i className={"align-middle ri-account-circle-fill"}></i>
              </span>
            </div>
            <div className="flex-grow-1 ms-3">
              <p className="text-uppercase fw-semibold fs-12 text-muted mb-1">
                Number of Contracts
              </p>
              <h4 className=" mb-0">
                {loading ? (
                  <Skeleton width={120} height={24} />
                ) : (
                  <CountUp
                    start={0}
                    end={totalContracts}
                    decimals={0}
                    separator={","}
                    prefix={""}
                    duration={1}
                  />
                )}
              </h4>
            </div>
          </div>
        </CardBody>
      </Card>
      <PaymentsModal
        isOpen={isModalOpen}
        toggle={toggleModal}
        title="Number of Contracts"
        filter={filter}
      />
    </Link>
  );
};

export default TotalContracts;
